// web/src/screens/card.ts — Game-1 card loop (plan 04-10).
//
// Wires mic singleton, waveform, audio_queue, preload, postTurn, and
// the navigate router into the full game loop per UI-SPEC §"Card screen"
// and §"Mic capture flow".
//
// D-09: Phase 4 ships only Game 1 (image cards) end-to-end.
// Games 2/4/5 text-effect helpers and Game 3 text-only layout are
// exported as throwing stubs — Phase 5 fills them in.
//
// D-16: mic_open_beep.m4a plays on every mic-open transition.
// UI-07: stream is NOT released on unmount — final.ts owns mic release.
// UI-09: next-card preload is handled server-side now; the v2 envelope
//        bundles the seed Task on action_type=next_card so no client-side
//        preloadNext is needed.
// Pitfall #8: user-select: none + -webkit-touch-callout: none on mic button.
// Pitfall #9: await playAudio() before card transition.

import { animate, type DOMKeyframesDefinition } from "motion";
import type { TurnEnvelope, TaskNextState, NextState, ActionType, Evaluation } from "../types/api.js";
import { acquireMic, newRecorder, getMime, releaseMic } from "../components/mic.js";
import { startWaveform } from "../components/waveform.js";
import type { WaveformHandle } from "../components/waveform.js";
import { playAudio } from "../components/audio_queue.js";
import { renderStack } from "../components/cumulative_card.js";
import { renderTextEffect } from "../components/text_effect.js";
import { isTurnTimeoutError, postTurn, postTurnInit, postTurnNoInput, sendTurnMetrics, TurnRequestError, type TurnTiming } from "../api/turn.js";
import { openTurnStream, isStreamingSupported, type TurnStream } from "../api/turn_stream.js";
import { postSkip } from "../api/skip.js";
import { postPause } from "../api/pause.js";
import { getSessionCurrent } from "../api/session.js";
import { finishSession } from "../api/sessions.js";
import { navigate } from "../main.js";
import { resolveMicBeepEnabled } from "./mic_beep_pref.js";

// Suppress unused-import warnings for symbols re-exported / used by callers.
// renderTextEffect is imported because plan 10-08 keeps it available in this
// module for revealMissingLetters interop and for any in-file fallbacks; the
// component itself lives in components/text_effect.ts. NextState/ActionType/
// Evaluation are exposed for type narrowing in switch arms.
type _Unused = NextState | ActionType | Evaluation;
void renderTextEffect;
void ({} as _Unused);

// CR-03: window.__SOFTFAIL_PRAISE_URL__ is the boot-resolved URL for the
// network-failure fallback praise audio ("Tu centies!"). The Go HTML shell
// computes it via Resolver.ResolvePraiseByKey at boot and injects it; the
// FE consumes it as an opaque string. Empty/undefined → silent fallback.
declare global {
  interface Window {
    __SOFTFAIL_PRAISE_URL__?: string;
    __RUNASPRIEKS_LAST_TURN_ERROR__?: unknown;
  }
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIC_BEEP_URL          = "/storage/system-audio/mic_open_beep.m4a"; // D-16
const SILENCE_RMS_THRESHOLD = 0.04;   // 0–1 normalised; tune during execution
const SILENCE_HOLD_MS       = 2000;   // 2 s silence → auto-stop, but only after speech has been detected
// SPEC_PAUSE_HOLD_MS — silence persistence that fires the speculative-eval
// `settling` signal: long enough to tell a real end-of-speech pause from an
// inter-word gap, short enough to leave most of the 2 s hold for the LLM
// round-trip to overlap. Only matters on the streaming transport with the
// server speculative_eval toggle on; a harmless no-op otherwise.
const SPEC_PAUSE_HOLD_MS    = 700;
const MAX_TURN_MS           = 8000;   // default mic-listen cap; overridden per /api/config/timeouts (T-04-10-02)
const MAX_CONSECUTIVE_FAILS = 3;      // parent banner threshold (UI-SPEC §Network error UX)

// Module-level consecutive-fail counter. Resets on each successful turn.
let consecutiveFails = 0;

// Streaming-STT sticky-disable: after this many consecutive streaming setup/turn
// failures we stop attempting streaming for the rest of the page lifetime and
// use the batch path directly, so a degraded network doesn't pay the
// streaming-setup tax every turn. Reset on the first streaming success.
const MAX_STREAM_FAILS = 2;
let streamConsecutiveFails = 0;
let streamingDisabled = false;

interface RecordedTurnAudio {
  blob: Blob;
  mime: string;
}

// ---------------------------------------------------------------------------
// Cumulative-stack reducer (D-02).
//
//  - action_type === null (init turn): seed stack with envelope.next_state
//  - "next_card":     reset stack to [envelope.next_state]
//  - "next_task":     append envelope.next_state
//  - "try_again":     no-op
//  - "game_complete": no-op (caller navigates to /final)
//
// Pure function — only `taskStack = applyEnvelopeToStack(...)` mutates the
// module-level stack (T-10-20: tampering mitigation, mutation goes through
// a single auditable path).
// ---------------------------------------------------------------------------
function applyEnvelopeToStack(
  stack: TaskNextState[],
  envelope: TurnEnvelope,
): TaskNextState[] {
  if (envelope.action_type === null) {
    return envelope.next_state.kind === "task" ? [envelope.next_state] : stack;
  }
  switch (envelope.action_type) {
    case "next_card":
      return envelope.next_state.kind === "task" ? [envelope.next_state] : stack;
    case "next_task":
      return envelope.next_state.kind === "task" ? [...stack, envelope.next_state] : stack;
    case "try_again":
    case "game_complete":
      return stack;
  }
}

/**
 * Resolve the active theme color used for the cumulative-stack tint and
 * waveform highlight. Priority order:
 *   1. sessionStorage.currentSession.themeColor (set by picker.ts in plan 10-09)
 *   2. getComputedStyle(document.body).getPropertyValue("--theme-color") — v1 fallback
 *   3. Hardcoded default "#5E3FBE" (matches v1 fallback at card.ts:536)
 *
 * Returning a concrete hex/rgb value (not a CSS var token) is required because
 * Motion One's animate() accepts only resolved color values in keyframes.
 */
function currentSessionThemeColor(): string {
  try {
    const raw = sessionStorage.getItem("currentSession");
    if (raw) {
      const sess = JSON.parse(raw) as { themeColor?: string };
      if (sess.themeColor && typeof sess.themeColor === "string") {
        return sess.themeColor;
      }
    }
  } catch {
    /* fall through to CSS-var resolution */
  }
  const fromCSS = getComputedStyle(document.body)
    .getPropertyValue("--theme-color")
    .trim();
  return fromCSS || "#5E3FBE";
}

// ---------------------------------------------------------------------------
// Game 5 reveal helper — operates on grapheme spans emitted by
// components/text_effect.ts. The selector is the contract.
// ---------------------------------------------------------------------------

/**
 * Game 5 reveal: fills each dashed-border box with its letter and animates
 * a glow + scale pop-in. Idempotent — boxes already containing text are
 * skipped, so subsequent turns on the same card (repetition phase) don't
 * re-trigger. Multiple boxes stagger in reading order.
 */
async function revealMissingLetters(
  container: HTMLElement,
  themeColor: string,
): Promise<void> {
  const boxes = Array.from(
    container.querySelectorAll<HTMLSpanElement>("[data-missing-letter-char]"),
  ).filter((b) => b.dataset.revealed === "false");
  if (boxes.length === 0) return;

  boxes.sort((a, b) => {
    const ai = Number(a.dataset.missingLetterIndex ?? "0");
    const bi = Number(b.dataset.missingLetterIndex ?? "0");
    return ai - bi;
  });

  const animateOne = async (box: HTMLSpanElement, idx: number): Promise<void> => {
    if (idx > 0) {
      await new Promise<void>((r) => setTimeout(r, idx * 120));
    }

    // Phase 1: glow halo — fire-and-forget so it overlaps with the pop-in.
    // Motion One needs a concrete color in the keyframes; var(--theme-color)
    // doesn't resolve mid-interpolation.
    animate(
      box as Element,
      {
        boxShadow: [
          `0 0 14px 4px transparent`,
          `0 0 14px 4px ${themeColor}`,
          `0 0 14px 4px transparent`,
        ],
      } as DOMKeyframesDefinition,
      { duration: 0.4, ease: "easeInOut" },
    );

    // Brief overlap so the glow precedes the letter
    await new Promise<void>((r) => setTimeout(r, 100));

    // Phase 2: unmask the letter and pop it in. The letter is already in
    // the span as transparent text (so layout reserved its actual width).
    box.dataset.revealed = "true";
    box.style.color = "var(--theme-color)";
    box.style.borderStyle = "solid";

    await animate(
      box as Element,
      {
        scale: [0.4, 1.2, 1],
        opacity: [0, 1, 1],
        rotate: ["-8deg", "0deg", "0deg"],
      } as DOMKeyframesDefinition,
      { duration: 0.35, ease: "easeOut" },
    );
  };

  await Promise.all(boxes.map((box, k) => animateOne(box, k)));
}

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

export function mount(root: HTMLElement, props?: unknown): () => void {
  const p = (props ?? {}) as { sessionId?: string };
  const sessionId = p.sessionId ?? "";
  if (!sessionId) {
    navigate("/picker");
    return () => {};
  }

  // Recover session metadata from sessionStorage (set by picker → intro flow).
  // v2 shape (plan 10-09 standardised): { sessionId, gameShortCode, themeColor }.
  // The first Task envelope is fetched server-side via postTurnInit (Phase 9
  // D-03) — the FE no longer caches a "first card" payload.
  let sess: { sessionId?: string; gameShortCode?: string; themeColor?: string } = {};
  try {
    sess = JSON.parse(sessionStorage.getItem("currentSession") ?? "{}") as {
      sessionId?: string;
      gameShortCode?: string;
      themeColor?: string;
    };
  } catch {
    /* ignore parse errors */
  }

  // Stale navigation guard: sessionStorage was cleared (typically after
  // endGameAndExit) but the user back-navigated into a leftover /play/:id
  // history entry. Bail out to picker rather than render an empty card.
  if (!sess.sessionId && !sessionId) {
    navigate("/picker");
    return () => {};
  }

  const gameShortCode: string = sess.gameShortCode ?? "vocab";
  let attemptInTurn = 1; // advisory FE counter; server is authoritative

  // Cumulative-Task stack (D-01 / D-02). Mutated only via applyEnvelopeToStack.
  let taskStack: TaskNextState[] = [];

  // 1-based card ordinal. The cumulative stack resets to length 1 on every
  // next_card transition, so taskStack.length can't be the card counter —
  // it's the within-card task-chain length. cardOrdinal is bumped only on
  // the init seed (→ 1) and on each next_card; it drives the ordinal badge
  // and the progress-row fill.
  let cardOrdinal = 0;

  document.body.className = `theme-${gameShortCode}`;

  // ---------------------------------------------------------------------------
  // Build static layout shell
  // ---------------------------------------------------------------------------

  root.innerHTML = `
    <section class="screen-card" style="
      --corner-c: calc(var(--space-lg) + 48px);
      min-height: 100dvh;
      background: var(--bg);
      position: relative;
      box-sizing: border-box;
      overflow-x: clip;
    ">
      <!-- Base layer: fills the whole screen and centers the image / text /
           image+text (renderStack flex-centres its column). The ordinal badge,
           mic, and progress row float on top of this. -->
      <div class="card-content" style="
        position: absolute;
        inset: 0;
        display: flex; align-items: center; justify-content: center;
        /* Extra bottom pad reserves clearance for the floating progress row
           (its bottom offset --space-xl + the 24px squares + a --space-md gap)
           so centred content never tucks under it on short screens. */
        padding: var(--space-lg) var(--space-lg)
                 calc(var(--space-xl) + 24px + var(--space-md)) var(--space-lg);
        min-height: 0; overflow: hidden;
        box-sizing: border-box;
      "></div>

      <!-- Floating overlays. --corner-c is the distance from the top and from
           the left/right edge to the CENTER of the corner elements; the
           translate(±50%) pins each element's own centre to that point
           regardless of its box size, so the 80px badge and the 96px mic button
           share one centre line and one inset, computed from their bounding
           boxes rather than hard-coded edge offsets. -->
      <div class="ordinal-badge" style="
        position: absolute;
        top: var(--corner-c); left: var(--corner-c);
        transform: translate(-50%, -50%);
        z-index: 3;
        width: 80px; height: 80px;
        background: var(--theme-color);
        color: var(--text-on-color);
        font-size: 56px; font-weight: 400;
        display: flex; align-items: center; justify-content: center;
        border-radius: 12px;
        letter-spacing: 0.02em;
        font-family: var(--font-fancy);
      "></div>

      <div class="progress-row" style="
        position: absolute;
        left: 0; right: 0;
        bottom: var(--space-xl);
        z-index: 3;
        display: flex; gap: var(--space-sm); justify-content: center;
        align-items: center;
      "></div>

      <div class="mic-area" style="
        position: absolute;
        top: var(--corner-c); right: var(--corner-c);
        transform: translate(50%, -50%);
        z-index: 4;
        display: flex; align-items: center; justify-content: center;
      ">
        <button
          class="mic-btn"
          aria-label="Mikrofons"
          style="
            width: 96px; height: 96px;
            border-radius: 50%;
            background: var(--bg);
            border: 3px solid var(--text-muted, #9aa0a6);
            font-size: 48px;
            cursor: pointer;
            display: flex; align-items: center; justify-content: center;
            user-select: none;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            filter: grayscale(1) opacity(0.55);
            transition: opacity 0.15s, filter 0.15s, border-color 0.15s, background 0.15s;
          "
        >🎤</button>
        <div class="mic-sparkles" aria-hidden="true">
          <span class="spark spark-1"></span>
          <span class="spark spark-2"></span>
          <span class="spark spark-3"></span>
          <span class="spark spark-4"></span>
          <span class="spark spark-5"></span>
        </div>
        <!-- Listening waveform floats just under the button so it never grows
             the mic-area box (which must stay 96px so the box centre == the
             button centre). JS toggles display:none/block. -->
        <canvas class="waveform" width="120" height="32" style="
          display: none;
          position: absolute;
          top: calc(100% + var(--space-sm));
          left: 50%;
          transform: translateX(-50%);
        "></canvas>
      </div>

      <p class="parent-banner" role="alert" style="
        display: none;
        position: absolute; top: 0; left: 0; right: 0;
        padding: var(--space-sm) var(--space-md);
        background: var(--surface);
        color: var(--text-muted);
        font-size: 12px; text-align: center;
        border-bottom: 1px solid #e0e0e0;
      ">Radās tehniska kļūda — pamēģini vēlreiz.</p>

      <div
        class="turn-timeout-overlay"
        data-timeout-retry
        role="dialog"
        aria-modal="true"
        aria-labelledby="turn-timeout-title"
        style="
          display: none;
          position: absolute;
          inset: 0;
          z-index: 5;
          background: rgba(255, 252, 244, 0.96);
          align-items: center;
          justify-content: center;
          padding: var(--space-lg);
          box-sizing: border-box;
        "
      >
        <div style="
          width: min(100%, 420px);
          background: #fffaf0;
          border: 3px solid var(--theme-color);
          border-radius: 24px;
          padding: clamp(24px, 6vw, 36px);
          box-shadow: 0 24px 60px rgba(20, 20, 20, 0.16);
          display: grid;
          gap: var(--space-lg);
          text-align: center;
        ">
          <p
            id="turn-timeout-title"
            style="
              margin: 0;
              font-family: var(--font-display);
              font-size: clamp(28px, 7vw, 40px);
              font-weight: 700;
              line-height: 1.15;
              color: var(--text-primary);
            "
          >Diemžēl notikusi kāda sistēmas kļūda.</p>
          <button
            type="button"
            class="turn-timeout-retry"
            data-retry-btn
            style="
              min-height: 72px;
              border: none;
              border-radius: 20px;
              background: var(--theme-color);
              color: var(--text-on-color);
              font-family: var(--font-display);
              font-size: clamp(24px, 5vw, 32px);
              font-weight: 700;
              padding: 16px 24px;
              cursor: pointer;
            "
          >Mēģināt vēlreiz</button>
        </div>
      </div>

      <div
        class="pause-overlay"
        data-pause-overlay
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-overlay-title"
        style="
          display: none;
          position: absolute;
          inset: 0;
          z-index: 6;
          background: rgba(255, 252, 244, 0.96);
          align-items: center;
          justify-content: center;
          padding: var(--space-lg);
          box-sizing: border-box;
        "
      >
        <div style="
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
          width: min(100%, 360px);
        ">
          <button
            type="button"
            class="pause-resume-btn"
            data-resume-btn
            id="pause-overlay-title"
            style="
              min-height: 96px;
              border: none;
              border-radius: 28px;
              background: var(--theme-color);
              color: var(--text-on-color);
              font-family: var(--font-fancy);
              font-size: clamp(28px, 7vw, 40px);
              font-weight: 700;
              padding: 20px 32px;
              cursor: pointer;
              box-shadow: 0 24px 60px rgba(20, 20, 20, 0.16);
            "
          >Turpināt spēli</button>
          <button
            type="button"
            class="pause-end-btn"
            style="
              min-height: 72px;
              border: 3px solid var(--theme-color);
              border-radius: 24px;
              background: var(--bg);
              color: var(--text-primary);
              font-family: var(--font-fancy);
              font-size: clamp(22px, 5.5vw, 32px);
              font-weight: 700;
              padding: 16px 24px;
              cursor: pointer;
            "
          >Beigt spēli</button>
        </div>
      </div>
    </section>
  `;

  const badge        = root.querySelector(".ordinal-badge")!   as HTMLElement;
  const cardContent  = root.querySelector(".card-content")!    as HTMLElement;
  const progressRow  = root.querySelector(".progress-row")!    as HTMLElement;
  const waveCanvas   = root.querySelector(".waveform")!        as HTMLCanvasElement;
  const micBtn       = root.querySelector(".mic-btn")!         as HTMLButtonElement;
  const micSparkles  = root.querySelector(".mic-sparkles")!   as HTMLElement;
  const parentBanner = root.querySelector(".parent-banner")!   as HTMLElement;
  const timeoutOverlay = root.querySelector(".turn-timeout-overlay")! as HTMLElement;
  const timeoutRetryBtn = root.querySelector(".turn-timeout-retry")! as HTMLButtonElement;
  const pauseOverlay   = root.querySelector(".pause-overlay")!     as HTMLElement;
  const pauseResumeBtn = root.querySelector(".pause-resume-btn")!  as HTMLButtonElement;
  const pauseEndBtn    = root.querySelector(".pause-end-btn")!     as HTMLButtonElement;

  // Mic visual states. "Off" (not listening) is gray + pale: muted border,
  // grayscale/faded emoji. "Listening" is the live theme-colored fill. Both
  // leave `opacity` untouched so the sending/paused dims compose on top.
  function setMicOff(): void {
    micBtn.classList.remove("mic-listening");
    micBtn.style.borderColor = "var(--text-muted, #9aa0a6)";
    micBtn.style.background   = "var(--bg)";
    micBtn.style.color        = "";
    micBtn.style.filter       = "grayscale(1) opacity(0.55)";
  }
  function setMicListening(): void {
    micBtn.style.borderColor = "var(--theme-color)";
    micBtn.style.background   = "var(--theme-color)";
    micBtn.style.color        = "var(--text-on-color)";
    micBtn.style.filter       = "none";
    micBtn.classList.add("mic-listening"); // fancy pulse (see .mic-listening in style.css)
  }

  // State flags
  let waveformHandle: WaveformHandle | null = null;
  let isRecording = false;
  let isPrompting = false;
  let isSending   = false;
  let unmounted   = false;
  let needsCardPrompt = false;
  let promptRunID = 0;
  let promptAbort: AbortController | null = null;
  let retryableTurnAudio: RecordedTurnAudio | null = null;
  let timeoutOverlayOpen = false;
  let timeoutOverlayRetrying = false;

  // Pause/resume state. The mic button is "on" by default — the prompt+
  // record cycle auto-starts on mount. Tapping the mic (or pressing the
  // browser back button) transitions to paused: in-flight prompt audio,
  // recorder, postTurn fetch, and praise playback are all aborted via
  // pauseAbort, and a full-screen overlay with "Turpināt spēli" /
  // "Beigt spēli" is shown. The current card remains unchanged because
  // the in-flight server-side FSM update fails on context cancellation
  // (the abort is the "stop message" — POST /pause additionally records
  // an audit log). "Beigt spēli" (or a second back press while paused)
  // routes through endGameAndExit → /picker.
  let isPaused = false;
  let pauseAbort: AbortController = new AbortController();
  let activeRecorder: MediaRecorder | null = null;
  // Live streaming-STT transport for the current turn (null on the batch path).
  // Aborted by pause/unmount alongside activeRecorder.
  let activeStream: TurnStream | null = null;

  // GUI-configurable mic-listen cap. Loaded once at mount from
  // /api/config/timeouts; falls back to MAX_TURN_MS on any error so the
  // card loop never blocks on an unreachable config endpoint.
  // Reveal settle (Game 5 missing_letter dwell after the pop-in animation,
  // migration 0046) ships from the same endpoint so card.ts only fetches once.
  let micListenMs = MAX_TURN_MS;
  let revealSettleMs = 3000;
  let perfTrackingEnabled = false; // Phase 2: gates the client perf beacon (default off until the fetch settles).
  void (async () => {
    try {
      const res = await fetch("/api/config/timeouts", { credentials: "include" });
      if (!res.ok) return;
      const t = await res.json() as {
        mic_listen_ms?: number;
        missing_letter_reveal_settle_ms?: number;
        perf_tracking_enabled?: boolean;
      };
      if (typeof t.mic_listen_ms === "number" && t.mic_listen_ms > 0) {
        micListenMs = t.mic_listen_ms;
      }
      if (typeof t.missing_letter_reveal_settle_ms === "number"
          && t.missing_letter_reveal_settle_ms >= 0) {
        revealSettleMs = t.missing_letter_reveal_settle_ms;
      }
      perfTrackingEnabled = t.perf_tracking_enabled === true;
    } catch { /* keep fallback */ }
  })();

  // Per-device mic-open-beep preference, resolved once at mount. Defaults
  // to false (no beep) until the resolver returns; the first turn's mic
  // open is therefore silent if the user lands on the card before the
  // fetch settles — acceptable since the beep is a nicety, not a contract.
  let micBeepEnabled = false;
  void (async () => {
    micBeepEnabled = await resolveMicBeepEnabled();
  })();

  // Theme color for waveform traffic-light tint and the cumulative-stack
  // accent. Resolved at mount via the v2 helper (sessionStorage themeColor
  // first, then CSS-var fallback). Captured once because animate() needs a
  // concrete color — see currentSessionThemeColor() docstring.
  const themeColor = currentSessionThemeColor();

  // Most recent task displayed at the top of the cumulative stack; persisted
  // for telemetry / log payloads (logTurnFailure consumes the short_code).
  function currentTopTask(): TaskNextState | undefined {
    return taskStack[taskStack.length - 1];
  }

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  function renderCurrentCard(): void {
    // Cumulative-Task render layer (D-01). The single source of truth is
    // taskStack — no in-card branching on task.kind / image_url /
    // word_text remains. Per-game variants (image-only Game 1, text-only
    // Game 3, image+text Games 4/5) are handled inside renderStack via
    // task.image_url / task.text_effect presence checks.
    cardContent.style.flexDirection = "";
    cardContent.style.gap = "";
    renderStack(taskStack, cardContent, themeColor);

    // Surface the latest task short_code on the screen-card root for
    // plan 10-12 micro-tests (pause-resume identity assertion).
    const screen = root.querySelector(".screen-card");
    if (screen) {
      const top = currentTopTask();
      if (top) {
        screen.setAttribute("data-task", top.task_short_code);
      } else {
        screen.removeAttribute("data-task");
      }
    }

    // Ordinal badge surfaces cardOrdinal so the parent sees a 1-based
    // counter that ticks per next_card transition. try_again / next_task
    // do not advance the badge.
    badge.textContent = String(Math.max(1, cardOrdinal));
    attemptInTurn = 1;

    renderProgressRow();
  }

  function renderProgressRow(): void {
    // Phase-5 D-Claude's-discretion: read totalCards from sessionStorage
    // (set by picker.ts on startSession success). Fall back to 5 (Phase-4
    // default) if absent — handles direct-navigation edge cases.
    let total = 5;
    const sessRaw = sessionStorage.getItem("currentSession");
    if (sessRaw) {
      try {
        const parsed = JSON.parse(sessRaw) as { totalCards?: number };
        if (typeof parsed.totalCards === "number" && parsed.totalCards > 0) {
          total = parsed.totalCards;
        }
      } catch {
        /* ignore parse error, keep fallback */
      }
    }
    progressRow.innerHTML = "";
    // cardOrdinal is the 1-based ordinal of the card currently on screen
    // (bumped on the init seed and each next_card). Using taskStack.length
    // here was the bug behind "only the first square ever fills" — the
    // cumulative-stack reducer resets the stack to length 1 on every
    // next_card, so that length never grows for image-card games.
    const ordinal = Math.max(1, cardOrdinal);
    for (let i = 1; i <= total; i++) {
      const sq = document.createElement("div");
      const filled  = i < ordinal;
      const current = i === ordinal;
      sq.style.cssText = `
        width: 24px; height: 24px;
        background: ${filled || current ? "var(--theme-color)" : "var(--bg)"};
        border: 2px solid var(--theme-color);
        border-radius: 4px;
        flex-shrink: 0;
      `;
      progressRow.appendChild(sq);
    }
  }

  function isAbortError(err: unknown): boolean {
    return err instanceof DOMException && err.name === "AbortError";
  }

  function isAutoplayBlocked(err: unknown): boolean {
    return err instanceof DOMException && err.name === "NotAllowedError";
  }

  function isTimeoutOverlayBlocking(): boolean {
    return timeoutOverlayOpen || timeoutOverlayRetrying;
  }

  function syncTimeoutOverlay(): void {
    const visible = timeoutOverlayOpen || timeoutOverlayRetrying;
    timeoutOverlay.style.display = visible ? "flex" : "none";
    timeoutRetryBtn.disabled = timeoutOverlayRetrying || retryableTurnAudio == null;
    timeoutRetryBtn.style.opacity = timeoutRetryBtn.disabled ? "0.6" : "1";
    timeoutRetryBtn.style.cursor = timeoutRetryBtn.disabled ? "default" : "pointer";
    micBtn.disabled = visible;
  }

  function hideTimeoutOverlay(): void {
    timeoutOverlayOpen = false;
    timeoutOverlayRetrying = false;
    retryableTurnAudio = null;
    syncTimeoutOverlay();
  }

  function showTimeoutOverlay(audio: RecordedTurnAudio): void {
    retryableTurnAudio = audio;
    timeoutOverlayOpen = true;
    timeoutOverlayRetrying = false;
    parentBanner.style.display = "none";
    parentBanner.removeAttribute("title");
    syncTimeoutOverlay();
  }

  function cancelPromptPlayback(): void {
    promptRunID++;
    if (promptAbort) {
      promptAbort.abort();
      promptAbort = null;
    }
    isPrompting = false;
  }

  function pauseGame(): void {
    if (isPaused || unmounted) return;
    isPaused = true;

    // Abort prompt audio (existing path).
    cancelPromptPlayback();

    // Abort the postTurn fetch + praise playback. This propagates
    // server-side via context cancellation: STT/LLM legs short-circuit,
    // and the FSM advance UPDATE in turn.go silently fails so the
    // session stays on the current card.
    pauseAbort.abort();

    // Stop the active recorder (if any). recordPromise resolves with
    // whatever was captured; the post-await isPaused check in
    // recordOneTurn skips sendTurnRequest.
    if (activeRecorder && activeRecorder.state === "recording") {
      try { activeRecorder.stop(); } catch { /* ignore */ }
    }
    // Abort the streaming transport (if any) so its socket + worklet tear down.
    activeStream?.abort();
    activeStream = null;

    // Tear down any other in-flight UI state so the overlay shows clean.
    waveformHandle?.stop();
    waveformHandle = null;
    waveCanvas.style.display = "none";
    if (timeoutOverlayOpen || timeoutOverlayRetrying) hideTimeoutOverlay();
    parentBanner.style.display = "none";
    parentBanner.removeAttribute("title");

    // Dim the mic button so it's clear the loop is not listening.
    setMicOff();
    micBtn.style.opacity = "0.6";

    // Show the resume overlay.
    pauseOverlay.style.display = "flex";

    // Best-effort audit signal — the abort above is what actually stops
    // any in-flight processing; this just records "pause was hit" in
    // slog so an operator can correlate the session-server view.
    if (sessionId) void postPause(sessionId);
  }

  async function resumeGame(): Promise<void> {
    if (!isPaused || unmounted) return;
    isPaused = false;

    // Recreate the abort controller for the new cycle. The fresh signal
    // is what we pass into the sync fetch below, so a re-pause during
    // the sync window aborts the in-flight GET cleanly.
    pauseAbort = new AbortController();

    // Hide overlay, restore mic visual.
    pauseOverlay.style.display = "none";
    micBtn.style.opacity = "1";

    // Reset cycle state. Earlier aborts left these in a partly-set state
    // (e.g. promptRunID was incremented, isPrompting was forced false).
    isPrompting = false;
    isRecording = false;
    isSending = false;
    needsCardPrompt = true;

    // Reconcile with the server's authoritative top-of-stack Task. Covers
    // the narrow window where the in-flight turn's FSM advance committed
    // server-side before the pause abort propagated — the client's
    // taskStack would then be stale. On any error (network, 5xx) we fall
    // through to the client's view rather than blocking resume.
    //
    // v2: server returns a TaskNextState (not the v1 {current_card, finished}
    // shape); identity is task_short_code. If the server's task differs from
    // our top-of-stack, reset the visible stack to [state] (one-row baseline)
    // — we cannot reconstruct the cumulative chain from a single snapshot.
    if (sessionId) {
      try {
        const state = await getSessionCurrent(sessionId, { signal: pauseAbort.signal });
        if (unmounted || isPaused) return;
        const last = currentTopTask();
        if (state.task_short_code !== last?.task_short_code) {
          taskStack = [state];
          renderCurrentCard();
        }
      } catch (err) {
        if (unmounted || isPaused) return;
        console.warn("resume sync failed — proceeding with client state", err);
      }
    }

    if (unmounted || isPaused) return;

    // Resume from a user gesture so autoplay-blocking browsers accept
    // the prompt audio playback.
    void playFreshCardPrompt(true);
  }

  /**
   * Replay the prompt audio for the top-of-stack Task and re-open the mic.
   * Used after pause-resume and after the autoplay-block fallback (the
   * very first prompt sometimes gets muted because the route transition
   * broke the gesture chain).
   *
   * Strict-serial sequence per D-06: prompt → mic. (No "speak" step here —
   * that lives in applyTurnEnvelope; this helper is for the case where
   * the server-side turn is already complete and we just need to re-prompt.)
   */
  async function playFreshCardPrompt(fromGesture: boolean): Promise<void> {
    const top = currentTopTask();
    if (!top || unmounted || isRecording || isPrompting || isSending || isTimeoutOverlayBlocking() || isPaused) return;

    needsCardPrompt = true;
    cancelPromptPlayback();

    const runID = promptRunID;
    const taskShortCode = top.task_short_code;
    const aborter = new AbortController();
    promptAbort = aborter;
    isPrompting = true;

    let shouldStartRecording = false;
    try {
      await playAudio(top.prompt_audio_url, { signal: aborter.signal });
      if (unmounted || runID !== promptRunID || currentTopTask()?.task_short_code !== taskShortCode) return;
      needsCardPrompt = false;
      shouldStartRecording = true;
    } catch (err) {
      if (unmounted || runID !== promptRunID) return;
      if (isAbortError(err)) return;

      if (isAutoplayBlocked(err) && !fromGesture) {
        console.info("card prompt playback needs a tap");
        return;
      }

      console.warn("card prompt audio failed", err);
      needsCardPrompt = false;
      shouldStartRecording = true;
    } finally {
      if (runID === promptRunID) {
        isPrompting = false;
        if (promptAbort === aborter) promptAbort = null;
      }
    }

    if (!shouldStartRecording || unmounted || runID !== promptRunID || currentTopTask()?.task_short_code !== taskShortCode) return;
    try {
      await recordOneTurn();
    } catch (err) {
      console.error(err);
    }
  }

  function onMicClick(): void {
    if (lpFired || unmounted) return;
    // While paused, only the overlay's "Turpināt spēli" / "Beigt spēli"
    // buttons act — mic clicks are ignored (the overlay sits above the
    // mic at z-index 6).
    if (isPaused) return;
    if (isTimeoutOverlayBlocking()) return;
    // Autoplay-block fallback: the very first card prompt may have been
    // muted by the browser if the gesture chain broke at the route
    // transition. When the loop is fully idle (nothing prompting,
    // recording, or sending) and the prompt still needs replay, this
    // tap unmutes it instead of pausing an already-silent game.
    if (needsCardPrompt && !isPrompting && !isRecording && !isSending) {
      void playFreshCardPrompt(true);
      return;
    }
    // Otherwise: mic = pause.
    pauseGame();
  }

  function logTurnFailure(err: unknown, blob: Blob, mime: string): void {
    const top = currentTopTask();
    const base = {
      sessionId,
      cardOrdinal: taskStack.length || null,
      cardShortCode: top?.task_short_code ?? null,
      attemptInTurn,
      consecutiveFails,
      mime,
      audioBytes: blob.size,
    };

    if (err instanceof TurnRequestError) {
      const payload = {
        kind: err.kind === "client_timeout" ? "client_timeout" : "http",
        ...base,
        status: err.status ?? null,
        code: err.code ?? null,
        requestId: err.requestId ?? null,
        responseMessage: err.responseMessage ?? null,
        body: err.body ?? null,
      };
      window.__RUNASPRIEKS_LAST_TURN_ERROR__ = payload;
      console.error("turn http error", JSON.stringify(payload), payload);
      return;
    }

    const payload = {
      kind: "transport",
      ...base,
      error: err instanceof Error
        ? {
            name: err.name,
            message: err.message,
            stack: err.stack ?? null,
          }
        : { message: String(err) },
    };
    window.__RUNASPRIEKS_LAST_TURN_ERROR__ = payload;
    console.error("turn transport error", JSON.stringify(payload), payload);
  }

  async function playSoftfailPraise(): Promise<void> {
    const softfailUrl = window.__SOFTFAIL_PRAISE_URL__;
    if (!softfailUrl) return;
    try {
      await playAudio(softfailUrl);
    } catch {
      /* swallow — offline */
    }
  }

  async function sendTurnRequest(blob: Blob, mime: string, onTiming?: (t: TurnTiming) => void): Promise<TurnEnvelope | null> {
    const recordedAudio: RecordedTurnAudio = { blob, mime };
    retryableTurnAudio = recordedAudio;
    isSending = true;
    micBtn.style.opacity = "0.5";
    micSparkles.classList.add("is-active"); // twinkle over the greyed mic while we evaluate
    syncTimeoutOverlay();

    try {
      const resp = await postTurn(sessionId, blob, mime, { signal: pauseAbort.signal, onTiming });
      consecutiveFails = 0;
      parentBanner.style.display = "none";
      parentBanner.removeAttribute("title");
      window.__RUNASPRIEKS_LAST_TURN_ERROR__ = undefined;
      hideTimeoutOverlay();
      return resp;
    } catch (err) {
      // If the user paused mid-fetch, the abort is intentional — silently
      // bail without logging a failure or surfacing the parent banner.
      if (isPaused) {
        retryableTurnAudio = null;
        return null;
      }

      logTurnFailure(err, blob, mime);

      if (isTurnTimeoutError(err)) {
        showTimeoutOverlay(recordedAudio);
        return null;
      }

      retryableTurnAudio = null;
      timeoutOverlayOpen = false;
      timeoutOverlayRetrying = false;
      syncTimeoutOverlay();

      if (err instanceof TurnRequestError) {
        // Server HTTP errors should stay visible to the parent instead of
        // being masked by the generic encouragement audio.
        consecutiveFails = 0;
        parentBanner.textContent = "Radās tehniska kļūda — pamēģini vēlreiz.";
        parentBanner.style.display = "block";
        if (err.requestId) {
          parentBanner.title = `Request ID: ${err.requestId}`;
        } else {
          parentBanner.removeAttribute("title");
        }
      } else {
        consecutiveFails++;
        if (consecutiveFails >= MAX_CONSECUTIVE_FAILS) {
          parentBanner.textContent = "Savienojuma problēma — pārbaudi internetu.";
          parentBanner.style.display = "block";
        }
        parentBanner.removeAttribute("title");
        await playSoftfailPraise();
      }

      return null;
    } finally {
      isSending = false;
      micBtn.style.opacity = "1";
      micSparkles.classList.remove("is-active");
      timeoutOverlayRetrying = false;
      syncTimeoutOverlay();
    }
  }

  /**
   * Strict-serial turn sequencer (D-06).
   *
   * Sequence:
   *   1. speak (envelope.speak_audio_url, optional)
   *   2. visible transition (cumulative-stack reducer + optional fade-out
   *      for next_card; D-02 + D-06 step 2)
   *   3. game-complete short-circuit → /final
   *   4. prompt audio (envelope.next_state.prompt_audio_url)
   *   5. mic open (recordOneTurn)
   *
   * Every await resolves on a real DOM event (`<audio>.ended` or motion
   * `.finished`). pauseAbort.signal is forwarded into both audio plays so
   * a mid-sequence pause unwinds cleanly.
   *
   * Game 5 reveal: when the top-of-stack task carries a missing_letter
   * effect and the action is anything other than try_again, fill the
   * dashed-border boxes after the speak step (mirrors the v1 contract;
   * gives the child visual closure on what the right answer was).
   */
  async function applyTurnEnvelope(envelope: TurnEnvelope, onFirstSound?: () => void): Promise<void> {
    // 1. Speak + (optionally) Game 5 visual reveal.
    //
    // Init turn (action_type === null) and try_again may include
    // speak_audio_url; some turns omit it. Wrap pause/abort gracefully.
    //
    // For missing_letter tasks on a settle action (anything but try_again /
    // init), the dashed-box reveal runs IN PARALLEL with the speak audio so
    // the spoken phrase and the visual reveal land together. The configured
    // missing_letter_reveal_settle_ms is the TOTAL dwell from the start of
    // speak/reveal to when we let the FSM transition proceed — the speak
    // duration counts toward it (a 3s dwell with a 2s speak only adds 1s).
    const topBeforeMutation = currentTopTask();
    const shouldReveal =
      topBeforeMutation?.text_effect?.type === "missing_letter" &&
      envelope.action_type !== "try_again" &&
      envelope.action_type !== null;

    const startedAt = performance.now();

    let speakPromise: Promise<void> = Promise.resolve();
    if (envelope.speak_audio_url) {
      // Praise gesture flourish — gentle scale pop on the current card
      // image while the praise audio plays. Cosmetic, fire-and-forget.
      if (cardContent.firstElementChild) {
        const praiseKf: DOMKeyframesDefinition = { scale: [1, 1.03, 1] };
        animate(
          cardContent.firstElementChild as Element,
          praiseKf,
          { duration: 0.2, ease: "easeOut" },
        );
      }
      speakPromise = playAudio(envelope.speak_audio_url, { signal: pauseAbort.signal, onPlaying: onFirstSound })
        .catch((e: unknown) => {
          if (isPaused) return;
          if (!isAbortError(e)) console.warn("speak failed", e);
        });
    }

    if (shouldReveal) {
      const revealPromise = revealMissingLetters(cardContent, themeColor);
      await Promise.all([speakPromise, revealPromise]);
      if (isPaused || unmounted) return;
      const remaining = revealSettleMs - (performance.now() - startedAt);
      if (remaining > 0) {
        await new Promise<void>((r) => setTimeout(r, remaining));
      }
    } else {
      await speakPromise;
    }
    if (isPaused || unmounted) return;

    // 2. Visible transition. Reducer first, fade-out gate (next_card only),
    //    then renderStack.
    const newStack = applyEnvelopeToStack(taskStack, envelope);
    // Card-ordinal bookkeeping — must happen before renderCurrentCard so the
    // badge + progress row reflect the new card. Only the init seed and a
    // next_card transition advance it; try_again / next_task stay on the
    // same card.
    if (envelope.action_type === null) {
      // Init turn: the server stamps card_ordinal so a full-page refresh
      // mid-session restores the correct badge/progress instead of snapping
      // back to 1. Fresh sessions report 1; older servers omit it → fall back.
      cardOrdinal =
        envelope.next_state.kind === "task"
          ? (envelope.next_state.card_ordinal ?? 1)
          : 1;
    } else if (envelope.action_type === "next_card") {
      cardOrdinal += 1;
    }
    if (envelope.action_type === "next_card") {
      await fadeOutCurrent();
      if (isPaused || unmounted) return;
    }
    taskStack = newStack;
    renderCurrentCard();

    // 3. Game-complete short-circuit. The reducer leaves the stack alone
    //    on game_complete; the navigation is the single side-effect.
    if (envelope.next_state.kind === "game_complete") {
      navigate(`/final/${encodeURIComponent(sessionId)}`);
      return;
    }

    // 4. Prompt audio. envelope.next_state is a TaskNextState here (the
    //    game_complete branch returned above).
    if (isPaused || unmounted) return;
    const promptUrl = envelope.next_state.prompt_audio_url;
    needsCardPrompt = true;
    cancelPromptPlayback();
    const runID = promptRunID;
    const aborter = new AbortController();
    promptAbort = aborter;
    isPrompting = true;
    try {
      await playAudio(promptUrl, { signal: aborter.signal, onPlaying: onFirstSound });
      if (unmounted || runID !== promptRunID) return;
      needsCardPrompt = false;
    } catch (e) {
      if (isPaused) return;
      if (isAbortError(e)) return;
      if (isAutoplayBlocked(e)) {
        // Caller (mic-tap fallback) will retry. Leave needsCardPrompt set.
        console.info("card prompt playback needs a tap");
        return;
      }
      console.warn("prompt failed", e);
      needsCardPrompt = false;
    } finally {
      if (runID === promptRunID) {
        isPrompting = false;
        if (promptAbort === aborter) promptAbort = null;
      }
    }
    if (isPaused || unmounted) return;

    // 5. Mic open + record one turn — unless this is a no-input-from-kid
    //    task (migration 0043). In that case we wait the per-task
    //    no_input_wait_ms (default 3000), then POST a JSON no-input turn
    //    and recurse on the resulting envelope. Visible state (image /
    //    displayed_text) stays put until the on_correct transition
    //    inside the recursive applyTurnEnvelope runs.
    if (envelope.next_state.kind === "task" && envelope.next_state.no_input_from_kid) {
      const waitMs = envelope.next_state.no_input_wait_ms ?? 3000;
      const aborted = await sleepAbortable(waitMs, pauseAbort.signal);
      if (aborted || isPaused || unmounted) return;
      let nextEnv: TurnEnvelope | null = null;
      try {
        nextEnv = await postTurnNoInput(sessionId, { signal: pauseAbort.signal });
      } catch (err) {
        if (isPaused || isAbortError(err)) return;
        console.error("no_input turn failed", err);
        return;
      }
      if (!nextEnv || isPaused || unmounted) return;
      await applyTurnEnvelope(nextEnv);
      return;
    }

    await recordOneTurn();
  }

  /**
   * sleepAbortable resolves after `ms` milliseconds, or earlier when the
   * supplied AbortSignal fires (returns `true` in that case). Used by
   * the no-input-from-kid path so a pause cancels the wait cleanly. We
   * roll our own rather than reaching for AbortSignal.timeout chained
   * with Promise.race so the timer is cancelled on abort — leaving a
   * dangling setTimeout would keep the loop alive past pauseGame.
   */
  function sleepAbortable(ms: number, signal: AbortSignal): Promise<boolean> {
    if (signal.aborted) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => {
      const handle = window.setTimeout(() => {
        signal.removeEventListener("abort", onAbort);
        resolve(false);
      }, ms);
      const onAbort = (): void => {
        window.clearTimeout(handle);
        signal.removeEventListener("abort", onAbort);
        resolve(true);
      };
      signal.addEventListener("abort", onAbort, { once: true });
    });
  }

  /**
   * fadeOutCurrent — fade out the topmost card image before swapping to
   * the next card (action_type === "next_card"). Lifted from v1's inline
   * fade at card.ts:1150-1156 (and a sibling site at :1343-1349 for the
   * skip path); consolidated into a single helper so the reducer caller
   * has one entry point.
   *
   * If `cardContent.firstElementChild` is null (first turn — empty stack),
   * skip the animation. Returns void after the animation resolves so the
   * renderStack mutation in applyTurnEnvelope step 2 happens AFTER the
   * visible transition (D-06: every await resolves on a real DOM event).
   */
  async function fadeOutCurrent(): Promise<void> {
    const target = cardContent.firstElementChild;
    if (!target) return;
    const fadeOutKf: DOMKeyframesDefinition = { opacity: [1, 0] };
    await animate(target as Element, fadeOutKf, { duration: 0.2, ease: "easeIn" });
  }

  // ---------------------------------------------------------------------------
  // Silence watcher — polls AnalyserNode RMS in rAF loop, stops on silence
  // ---------------------------------------------------------------------------

  // startSilenceWatcher polls the analyser and calls onFinalize() once at
  // end-of-speech (RMS + 2s hold) or the hard cap. onFinalize is the single
  // end-of-speech hook for BOTH transports: batch wires it to recorder.stop();
  // streaming wires it to recorder.stop() + stream.finalize(). It is invoked at
  // most once (guarded by the caller flipping isRecording / the recorder state).
  function startSilenceWatcher(
    analyser: AnalyserNode,
    onFinalize: () => void,
    started: number,
    marks: { lastNonSilent: number; heardSpeech: boolean },
    onPause?: () => void,
    onResume?: () => void,
  ): void {
    const buf = new Uint8Array(analyser.fftSize);
    let pauseSignalled = false; // fired onPause for the current silence stretch

    function tick(): void {
      if (!isRecording) return;

      analyser.getByteTimeDomainData(buf);
      let sumSq = 0;
      for (let i = 0; i < buf.length; i++) {
        const sample = buf[i]!;
        const v = (sample - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / buf.length);

      const now = performance.now();
      if (rms > SILENCE_RMS_THRESHOLD) {
        // Speech (resumed): cancel any speculative eval started during a dip.
        if (pauseSignalled) {
          pauseSignalled = false;
          onResume?.();
        }
        marks.lastNonSilent = now;
        marks.heardSpeech = true;
      }

      const elapsed = now - started;
      const silentFor = now - marks.lastNonSilent;

      // Speculative-eval kick: once silence has persisted past the speculative
      // threshold, tell the server to start evaluating the interim transcript so
      // its round-trip overlaps the remaining hold. Idempotent per silence
      // stretch; reset when speech resumes (above).
      if (marks.heardSpeech && !pauseSignalled && silentFor > SPEC_PAUSE_HOLD_MS) {
        pauseSignalled = true;
        onPause?.();
      }

      // Auto-stop on silence — only after the child has actually said something.
      // If we never hear speech, fall through to the hard cap below.
      if (marks.heardSpeech && silentFor > SILENCE_HOLD_MS) {
        onFinalize();
        return;
      }
      // Hard cap — T-04-10-02. Operator-tunable via /config (mic_listen_ms).
      if (elapsed > micListenMs) {
        onFinalize();
        return;
      }

      requestAnimationFrame(tick);
    }

    requestAnimationFrame(tick);
  }

  // ---------------------------------------------------------------------------
  // Core turn loop
  // ---------------------------------------------------------------------------

  // emitTurnPerf logs the per-turn latency breakdown (Phase 1). Called once per
  // turn from onFirstSound — by then every upstream mark is known. The Phase 2
  // beacon is wired in here behind the perf-tracking flag.
  function emitTurnPerf(resp: TurnEnvelope, m: {
    lastNonSilent: number; heardSpeech: boolean; appliedAt: number;
    firstSound: number | null; finalizeAt: number; transport: "streaming" | "batch";
    roundtripMs?: number; networkMs?: number; serverTotalMs?: number;
  }): void {
    const audioLegMs = m.firstSound != null ? Math.round(m.firstSound - m.appliedAt) : undefined;
    const perceivedMs = (m.heardSpeech && m.firstSound != null)
      ? Math.round(m.firstSound - m.lastNonSilent) : undefined;
    const pipelineMs = perceivedMs != null ? perceivedMs - SILENCE_HOLD_MS : undefined;
    // finalizeToEnvelopeMs: end-of-speech → envelope applied. The headline
    // streaming win (STT overlaps speech, so only the finalize tail counts).
    const finalizeToEnvelopeMs = (m.finalizeAt > 0 && m.appliedAt > 0)
      ? Math.round(m.appliedAt - m.finalizeAt) : undefined;
    console.info("[turn] perf", {
      transport: m.transport,
      perceivedMs, pipelineMs, audioLegMs, finalizeToEnvelopeMs,
      roundtripMs: m.roundtripMs, networkMs: m.networkMs, serverTotalMs: m.serverTotalMs,
    });
    // Phase 2: beacon to the server, gated on the operator flag (the client must
    // not send when off) and on having an attempt row to attach to.
    if (perfTrackingEnabled && resp.attempt_id) {
      sendTurnMetrics(sessionId, resp.attempt_id, {
        client_roundtrip_ms: m.roundtripMs,
        client_network_ms: m.networkMs,
        client_audio_first_sound_ms: audioLegMs,
        client_perceived_ms: perceivedMs,
        client_pipeline_ms: pipelineMs,
        client_transport: m.transport,
        client_finalize_to_envelope_ms: finalizeToEnvelopeMs,
      });
    }
  }

  async function recordOneTurn(): Promise<void> {
    if (isRecording || isPrompting || isSending || unmounted || isTimeoutOverlayBlocking() || isPaused) return;

    // Acquire mic singleton (no new getUserMedia if already held — UI-07)
    const { analyser } = await acquireMic();
    if (isPaused || unmounted) return;

    // Mic-open beep per D-16. Gated on the per-device preference (default
    // off; admin-tunable default + per-device localStorage override). When
    // disabled, recording starts immediately with no beep.
    if (micBeepEnabled) {
      try { await playAudio(MIC_BEEP_URL); } catch { /* non-fatal */ }
      if (isPaused || unmounted) return; // guard against pause/unmount during beep
    }

    // ---- Enter LISTENING state -----------------------------------------------
    waveCanvas.style.display = "block";
    waveformHandle = startWaveform(waveCanvas, analyser, themeColor);
    setMicListening();
    micBtn.style.opacity    = "1";
    const micKf: DOMKeyframesDefinition = { scale: [1, 1.05, 1] };
    animate(micBtn as Element, micKf, { duration: 0.1 });

    const recorder = newRecorder();
    activeRecorder = recorder; // expose for pauseGame to stop mid-record
    const chunks: Blob[] = [];

    recorder.addEventListener("dataavailable", (e: BlobEvent) => {
      if (e.data.size > 0) chunks.push(e.data);
    });

    const recordPromise = new Promise<Blob>((resolve, reject) => {
      recorder.addEventListener("stop",  () => {
        resolve(new Blob(chunks, { type: getMime() }));
      }, { once: true });
      recorder.addEventListener("error", () => {
        reject(new Error("MediaRecorder error"));
      }, { once: true });
    });

    // Per-turn perf marks (Phase 1 diagnostics): lastNonSilent/heardSpeech from
    // the silence watcher, roundtrip/network from postTurn's onTiming, firstSound
    // from the response audio's `playing` event. All upstream marks are known by
    // the time any response audio plays, so we emit from onFirstSound.
    const turnMetrics = {
      lastNonSilent: 0,
      heardSpeech: false,
      appliedAt: 0,
      firstSound: null as number | null,
      finalizeAt: 0,
      transport: "batch" as "streaming" | "batch",
      roundtripMs: undefined as number | undefined,
      networkMs: undefined as number | undefined,
      serverTotalMs: undefined as number | undefined,
    };

    // Start the fallback MediaRecorder FIRST so it captures from t=0, in
    // parallel with the streaming WebSocket opening. The recorder is the batch
    // fallback buffer: if streaming fails we POST its blob to the batch endpoint
    // and the child never re-records.
    isRecording = true;
    const started = performance.now();
    recorder.start();

    // Open the streaming transport. openTurnStream is non-blocking — it returns
    // before the socket finishes opening, with the worklet already tapping the
    // mic and buffering the onset, so the WS round-trip stays off the prompt→mic
    // critical path and the child's first word is never dropped.
    let stream: TurnStream | null = null;
    if (isStreamingSupported() && !streamingDisabled) {
      try {
        stream = await openTurnStream(sessionId);
        turnMetrics.transport = "streaming";
      } catch (e) {
        stream = null;
        noteStreamFailure();
        console.warn("[turn] streaming setup failed → batch", e);
      }
      if (isPaused || unmounted) {
        // Recorder + waveform are torn down by pauseGame/unmount; clear the
        // recording flag (and stop the recorder as a safety net) so the next
        // turn isn't blocked by a stale isRecording.
        stream?.abort();
        if (recorder.state === "recording") recorder.stop();
        isRecording = false;
        return;
      }
    }
    activeStream = stream;
    // onFinalize is the single end-of-speech hook: stop the fallback recorder
    // AND (when streaming) tell the server to finalize. Always awaiting
    // recordPromise below keeps the LISTENING→SENDING transition identical to
    // the batch path and guarantees the fallback blob is ready.
    const onFinalize = (): void => {
      if (turnMetrics.finalizeAt === 0) turnMetrics.finalizeAt = performance.now();
      if (recorder.state === "recording") recorder.stop();
      stream?.finalize();
    };
    startSilenceWatcher(
      analyser, onFinalize, started, turnMetrics,
      () => stream?.signalPause(),
      () => stream?.signalResume(),
    );

    let blob: Blob;
    try {
      blob = await recordPromise;
    } finally {
      // ---- Enter SENDING state -----------------------------------------------
      isRecording = false;
      activeRecorder = null;
      waveformHandle?.stop();
      waveformHandle = null;
      waveCanvas.style.display = "none";
      setMicOff();
    }

    if (isPaused || unmounted) { stream?.abort(); activeStream = null; return; } // pause discarded this turn

    const mime = getMime();
    const onTiming = (t: TurnTiming): void => {
      turnMetrics.roundtripMs = t.roundtripMs;
      turnMetrics.networkMs = t.networkMs;
      turnMetrics.serverTotalMs = t.serverTotalMs;
    };

    let resp: TurnEnvelope | null = null;
    if (stream) {
      // Sparkle over the greyed mic while the server evaluates over the WS. The
      // batch path gets this from sendTurnRequest, but the streaming success
      // path never goes through it. .finally clears the stars on resolve/reject
      // (incl. the early-returns below) without disturbing result()'s type.
      micSparkles.classList.add("is-active");
      const streamResult = await stream.result().finally(() => {
        micSparkles.classList.remove("is-active");
      });
      activeStream = null;
      if (isPaused || unmounted) return;
      if (streamResult) {
        noteStreamSuccess();
        resp = streamResult.envelope;
        turnMetrics.serverTotalMs = streamResult.serverTotalMs;
      } else {
        // Streaming failed — fall back to the batch POST using the buffered blob.
        noteStreamFailure();
        turnMetrics.transport = "batch";
        resp = await sendTurnRequest(blob, mime, onTiming);
      }
    } else {
      resp = await sendTurnRequest(blob, mime, onTiming);
    }
    if (!resp) return;
    const envelope = resp; // const alias so the closure keeps the non-null narrowing
    turnMetrics.appliedAt = performance.now();
    let emitted = false;
    const onFirstSound = (): void => {
      if (emitted) return;
      emitted = true;
      turnMetrics.firstSound = performance.now();
      emitTurnPerf(envelope, turnMetrics);
    };
    await applyTurnEnvelope(envelope, onFirstSound);
  }

  // Streaming sticky-disable bookkeeping. After MAX_STREAM_FAILS consecutive
  // failures we stop attempting streaming for the rest of the page lifetime.
  function noteStreamFailure(): void {
    streamConsecutiveFails++;
    if (streamConsecutiveFails >= MAX_STREAM_FAILS) {
      streamingDisabled = true;
      console.warn("[turn] streaming sticky-disabled after", streamConsecutiveFails, "failures");
    }
  }
  function noteStreamSuccess(): void {
    streamConsecutiveFails = 0;
  }

  async function retryTimedOutTurn(): Promise<void> {
    const recordedAudio = retryableTurnAudio;
    if (!recordedAudio || timeoutOverlayRetrying || isSending || unmounted) return;

    timeoutOverlayOpen = true;
    timeoutOverlayRetrying = true;
    syncTimeoutOverlay();

    const resp = await sendTurnRequest(recordedAudio.blob, recordedAudio.mime);
    if (!resp) return;
    await applyTurnEnvelope(resp);
  }

  function onTimeoutRetryClick(): void {
    void retryTimedOutTurn().catch(console.error);
  }

  function onPauseResumeClick(): void {
    void resumeGame().catch(console.error);
  }

  function onPauseEndClick(): void {
    void endGameAndExit().catch(console.error);
  }

  // Ends the session and returns to the picker. Reached via the
  // "Beigt spēli" overlay button or via a second browser back press
  // while already paused. Tears down in-flight state, fires a
  // best-effort POST /finish, releases the mic (matching final.ts
  // UI-07), clears the cached session, and pushes /picker. The mic
  // release here is intentional and parallel to the natural finish
  // path — this is the session ending, just at a different exit.
  async function endGameAndExit(): Promise<void> {
    if (unmounted) return;

    unmounted = true;
    isPaused = true;
    cancelPromptPlayback();
    pauseAbort.abort();
    if (activeRecorder && activeRecorder.state === "recording") {
      try { activeRecorder.stop(); } catch { /* ignore */ }
    }
    activeStream?.abort();
    activeStream = null;
    waveformHandle?.stop();
    waveformHandle = null;

    if (sessionId) {
      void finishSession(sessionId).catch(() => { /* abandon path — best-effort */ });
    }
    void releaseMic().catch(() => { /* non-fatal */ });

    sessionStorage.removeItem("currentSession");

    if (window.__onBackPress__ === onBackPress) {
      delete window.__onBackPress__;
    }

    navigate("/picker");
  }

  // Back-button hook installed on window.__onBackPress__. The router's
  // popstate listener calls this before its default URL dispatch; a
  // true return suppresses dispatch. We pair this with a same-URL
  // history trap (pushed below) so the browser back button fires
  // popstate without ever changing the address bar away from /play/:id.
  // - First back press during play  → pause + re-arm trap.
  // - Back press while paused       → endGameAndExit (returns to /picker).
  function onBackPress(): boolean {
    if (unmounted) return false;

    if (!isPaused) {
      pauseGame();
      // Re-arm the trap so the next back press is also intercepted.
      // Same URL, so the browser address bar does not move.
      history.pushState({ playTrap: true }, "", location.pathname);
      return true;
    }

    void endGameAndExit().catch(console.error);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Initial render — Phase 9 D-03 init turn.
  //
  // The server is authoritative on Task identity (Phase 9 D-03). Client
  // POSTs an empty application/octet-stream body to /api/sessions/:id/turns;
  // server detects no multipart audio and returns the seed TaskNextState
  // envelope WITHOUT writing an Attempt row. We then run the standard
  // strict-serial sequencer (applyTurnEnvelope), which seeds the cumulative
  // stack via the reducer's action_type === null branch and proceeds to
  // prompt → mic.
  //
  // Some browsers treat the intro-screen timeout navigation as outside the
  // gesture chain, so autoplay may still be blocked there; in that case
  // the prompt step inside applyTurnEnvelope catches NotAllowedError and
  // leaves needsCardPrompt set, and the first mic tap (onMicClick) replays
  // the prompt and only then opens the mic.
  // ---------------------------------------------------------------------------

  syncTimeoutOverlay();
  void (async () => {
    try {
      const initEnvelope = await postTurnInit(sessionId);
      if (unmounted) return;
      await applyTurnEnvelope(initEnvelope);
    } catch (err) {
      if (unmounted) return;
      console.error("init turn failed", err);
      // Surface the failure to the parent so they can retry by tapping mic
      // (which falls through to playFreshCardPrompt — a no-op until the
      // stack is seeded). The simpler recovery is for the parent to back out.
      parentBanner.textContent = "Radās tehniska kļūda — pamēģini vēlreiz.";
      parentBanner.style.display = "block";
    }
  })();
  micBtn.addEventListener("click", onMicClick);
  timeoutRetryBtn.addEventListener("click", onTimeoutRetryClick);
  pauseResumeBtn.addEventListener("click", onPauseResumeClick);
  pauseEndBtn.addEventListener("click", onPauseEndClick);

  // Arm the back-button trap. Pushes a same-URL history entry so the
  // first browser back press pops back to this same /play/:id URL,
  // letting onBackPress convert it into a pause instead of leaking
  // through to the previous screen.
  history.pushState({ playTrap: true }, "", location.pathname);
  window.__onBackPress__ = onBackPress;

  // ---------------------------------------------------------------------------
  // UI-05 long-press skip (Phase 5).
  // 2-second hold on mic = parent skip → POST /api/sessions/:id/skip
  // (audit-clean Attempt with guard_reason='parent_skip' per plan 05-01 Task 3).
  // Audit-clean per checker B4/B6: dedicated /skip endpoint writes the Attempt.
  // ---------------------------------------------------------------------------
  // TODO(post-beta): extract to web/src/components/long_press.ts (RESEARCH §Project Structure)
  const LONG_PRESS_MS = 2000;
  const RING_R = 44;
  const ringCircumference = 2 * Math.PI * RING_R;

  // W3 layout note: ring is position:absolute inset:0 with pointer-events:none,
  // attached to the mic button's parent — the existing .mic-row layout
  // (waveform canvas + network-error banner siblings) is unaffected because
  // the overlay does not consume layout space.
  // Long-press skip-ring overlay (data-skip-ring) — plan 10-12 micro-test selector.
  const ringWrap = document.createElement("div");
  ringWrap.setAttribute("data-skip-ring", "");
  ringWrap.style.cssText = "position:absolute;width:96px;height:96px;pointer-events:none;";
  ringWrap.innerHTML = `
    <svg width="96" height="96" style="position:absolute;top:0;left:0;pointer-events:none;">
      <circle r="44" cx="48" cy="48" stroke="var(--theme-color, #5E3FBE)" stroke-width="4"
        fill="none" stroke-dasharray="${ringCircumference}" stroke-dashoffset="${ringCircumference}"
        transform="rotate(-90 48 48)" class="lp-ring" />
    </svg>
  `;
  // Ensure the ring overlays the mic button. Append to micBtn's parent and
  // position absolutely; the parent (.mic-row) is a flex column so we make
  // it position:relative if it isn't already.
  const micParent = micBtn.parentElement!;
  if (getComputedStyle(micParent).position === "static") {
    micParent.style.position = "relative";
  }
  micBtn.style.position = "relative";
  // Anchor the ring on the mic button itself for precise alignment.
  micBtn.appendChild(ringWrap);
  ringWrap.style.top = "0";
  ringWrap.style.left = "0";
  const ringCircle = ringWrap.querySelector(".lp-ring") as SVGCircleElement;

  let lpTimer: number | null = null;
  let lpRaf: number | null = null;
  let lpStart = 0;
  let lpFired = false;

  function lpTick(): void {
    const elapsed = performance.now() - lpStart;
    const frac = Math.min(elapsed / LONG_PRESS_MS, 1);
    ringCircle.style.strokeDashoffset = `${ringCircumference * (1 - frac)}`;
    if (frac < 1 && !lpFired) lpRaf = requestAnimationFrame(lpTick);
  }

  function lpCancel(): void {
    if (lpTimer != null) { clearTimeout(lpTimer); lpTimer = null; }
    if (lpRaf != null) { cancelAnimationFrame(lpRaf); lpRaf = null; }
    ringCircle.style.strokeDashoffset = `${ringCircumference}`;
  }

  async function lpSkip(): Promise<void> {
    if (isTimeoutOverlayBlocking()) return;
    lpFired = true;
    lpCancel();
    if (!sessionId) return;
    try {
      // v2: /skip returns the standard TurnEnvelope (Phase 9). Feed it
      // through the same strict-serial sequencer so the parent-skip path
      // is visually identical to a normal advance — speak (if any) →
      // fadeOut → renderStack(reset) → prompt → mic.
      cancelPromptPlayback();
      const resp = await postSkip(sessionId);
      await applyTurnEnvelope(resp);
    } catch {
      // Silent — parent simply taps again or moves on.
    }
  }

  function lpStartHandler(e: PointerEvent): void {
    if (isTimeoutOverlayBlocking() || isSending || isPrompting || isPaused) return;
    e.preventDefault(); // suppress contextmenu on iOS (Pitfall 7)
    lpFired = false;
    lpStart = performance.now();
    ringCircle.style.strokeDashoffset = `${ringCircumference}`;
    lpRaf = requestAnimationFrame(lpTick);
    lpTimer = window.setTimeout(() => { lpTimer = null; void lpSkip(); }, LONG_PRESS_MS);
  }

  function lpContextMenu(e: Event): void { e.preventDefault(); }

  micBtn.addEventListener("pointerdown", lpStartHandler);
  micBtn.addEventListener("pointerup", lpCancel);
  micBtn.addEventListener("pointercancel", lpCancel);
  micBtn.addEventListener("pointerleave", lpCancel);
  micBtn.addEventListener("contextmenu", lpContextMenu);

  // ---------------------------------------------------------------------------
  // Unmount
  // DO NOT release the mic here (UI-07) — the mic stream is owned by
  // final.ts (POST /finish + window unload). Releasing here would revoke
  // the iOS implicit grant and force the OS permission sheet next session.
  // ---------------------------------------------------------------------------

  return (): void => {
    unmounted = true;
    cancelPromptPlayback();
    pauseAbort.abort(); // cancel any in-flight postTurn / praise audio
    if (activeRecorder && activeRecorder.state === "recording") {
      try { activeRecorder.stop(); } catch { /* ignore */ }
    }
    activeStream?.abort();
    activeStream = null;
    waveformHandle?.stop();
    lpCancel();
    if (window.__onBackPress__ === onBackPress) {
      delete window.__onBackPress__;
    }
    micBtn.removeEventListener("click", onMicClick);
    timeoutRetryBtn.removeEventListener("click", onTimeoutRetryClick);
    pauseResumeBtn.removeEventListener("click", onPauseResumeClick);
    pauseEndBtn.removeEventListener("click", onPauseEndClick);
    micBtn.removeEventListener("pointerdown", lpStartHandler);
    micBtn.removeEventListener("pointerup", lpCancel);
    micBtn.removeEventListener("pointercancel", lpCancel);
    micBtn.removeEventListener("pointerleave", lpCancel);
    micBtn.removeEventListener("contextmenu", lpContextMenu);
    ringWrap.remove();
    // mic singleton remains alive for the next card or final screen
  };
}
